import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '@/store/notificationStore'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const Notifications = () => {
  const { t } = useTranslation(['dashboard', 'common'])
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead
  } = useNotificationStore()

  const handleNotificationClick = (notification: any) => {
    // 읽지 않은 알림이면 읽음 처리
    if (!notification.is_read) {
      markAsRead(notification.id)
    }

    // 알림 타입에 따라 적절한 페이지로 이동
    const type = notification.type?.toLowerCase() || ''

    if (type.includes('deposit') || type.includes(t('assetFlow.deposit'))) {
      navigate('/admin/deposits-withdrawals-management')
    } else if (type.includes('withdrawal') || type.includes(t('assetFlow.withdrawal'))) {
      navigate('/admin/deposits-withdrawals-management')
    } else if (type.includes('user') || type.includes(t('general.k_6745df')) || type.includes(t('general.k_4204e5'))) {
      navigate('/admin/users')
    } else if (type.includes('inquiry') || type.includes(t('header:inquiries'))) {
      navigate('/admin/inquiries')
    } else if (type.includes('chat') || type.includes(t('trading:settings.chatNotification'))) {
      navigate('/admin/chat')
    } else if (type.includes('partner')) {
      if (type.includes('withdrawal')) {
        navigate('/admin/partner-withdrawals')
      } else {
        navigate('/admin/partners')
      }
    } else if (type.includes('notice') || type.includes(t('general.k_0b1a0f'))) {
      navigate('/admin/notices')
    } else if (type.includes('event') || type.includes(t('general.k_8d0f5c'))) {
      navigate('/admin/events')
    } else if (type.includes('staking') || type.includes(t('stats.staking'))) {
      navigate('/admin/staking')
    } else {
      // 기본적으로 대시보드로 이동
      console.log('Unknown notification type:', type)
    }
  }

  const formatNotificationTime = (isoString: string): string => {
    const now = new Date()

    // 백엔드 REST는 naive UTC(Z 없음) → Z 추가. 라이브 WS 알림은 '+09:00' 등 tz 포함 → 그대로 사용 (Z 덧붙이면 Invalid Date)
    const utcString = /(Z|[+-]\d{2}:?\d{2})$/.test(isoString) ? isoString : isoString + 'Z'
    const notificationTime = new Date(utcString)

    const diffInSeconds = Math.floor((now.getTime() - notificationTime.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return t('common:time.justNow')
    } else if (diffInSeconds < 3600) {
      return t('general.k_d23758', { var0: Math.floor(diffInSeconds / 60) })
    } else if (diffInSeconds < 86400) {
      return t('general.k_676386', { var0: Math.floor(diffInSeconds / 3600) })
    } else if (diffInSeconds < 2592000) {
      return t('general.k_753490', { var0: Math.floor(diffInSeconds / 86400) })
    } else {
      return notificationTime.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }

  return (
    <div className="glass-card p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2 flex items-center">
              <Bell className="w-5 h-5 mr-2" />{t('dashboard:notifications.label')}</h2>
            <p className="text-xs sm:text-sm text-dark-400">
              {unreadCount > 0
                ? t('general.k_fe5573', { unreadCount: unreadCount })
                : t('notifications.allRead')
              }
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <>
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-primary-400 hover:text-primary-300 font-medium"
                >{t('dashboard:notifications.markAllRead')}</button>
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-sm text-white font-bold">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto space-y-2">
        {notifications.length > 0 ? (
          notifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => handleNotificationClick(notification)}
              className={`p-3 sm:p-4 rounded-xl hover:bg-white/5 transition-all duration-200 cursor-pointer border ${
                notification.is_read
                  ? 'border-white/5 bg-dark-900/30'
                  : 'border-primary-500/30 bg-primary-500/5'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                  notification.is_read ? 'bg-dark-600' : 'bg-primary-500'
                }`}></div>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    notification.is_read ? 'text-dark-300' : 'text-white'
                  }`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-dark-400 mt-1 line-clamp-2">
                    {notification.content}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-dark-500">
                      {formatNotificationTime(notification.created_at)}
                    </p>
                    {notification.type && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-dark-700 text-dark-300">
                        {notification.type}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-dark-600 mx-auto mb-4" />
            <p className="text-sm text-dark-400">{t('dashboard:notifications.noNotifications')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Notifications
